import React, { useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import {
    ArrowUpIcon,
    ArrowDownIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations';
import FixedHorizontalScroll from './FixedHorizontalScroll';

const DataTable = ({
    data,
    columns,
    isLoading,
    onRowClick,
    onRowDoubleClick,
    selectedRowId = null,
    rowIdAccessor = 'id',
    stickyColumnCount = 0,
    showSearch = true,
    compact = false,
    /** When compact, use wrapped multi-line cells instead of narrow single-line truncate (wide lists). */
    compactCellWrap = false,
    enablePagination = false,
    initialPageSize = 10,
    pageSizeOptions = [10, 25, 50, 100],
    /** Grid yatay kaydırma çubuğunu viewport altına sabitler (sayfa düzeni değişmez). */
    fixedHorizontalScroll = false,
    /** Uzun metin sütunlarında sarmalamayı kolaylaştırır */
    fixedTableLayout = false,
    /** Teal gradient header + softer borders for vehicle module pages. */
    modern = false,
    /** Soft zebra stripes for row readability. */
    softStriped = modern,
    getRowClassName = null,
    /** Sunucu taraflı sıralama: parent sorting state'ini kontrol eder, tablo kendi sıralamaz. */
    manualSorting = false,
    sorting: controlledSorting,
    onSortingChange: controlledOnSortingChange,
}) => {
    const [internalSorting, setInternalSorting] = useState([]);
    const sorting = manualSorting ? (controlledSorting || []) : internalSorting;
    const handleSortingChange = manualSorting ? controlledOnSortingChange : setInternalSorting;
    const [globalFilter, setGlobalFilter] = useState('');
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: initialPageSize,
    });
    const { language } = useLanguage();
    const { t } = useTranslation(language);

    const table = useReactTable({
        data,
        columns,
        state: { sorting, globalFilter, pagination },
        manualSorting,
        onSortingChange: handleSortingChange,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    });

    const isDenseModern = modern && compact;
    const tableTextClass = isDenseModern ? 'text-[10px]' : compact ? 'text-[11px]' : 'text-sm';
    const headerPaddingClass = isDenseModern ? 'px-1.5 py-1' : compact ? 'px-2 py-1.5' : 'px-4 py-3';
    const cellPaddingClass = isDenseModern ? 'px-1.5 py-1' : compact ? 'px-2 py-1.5' : 'px-4 py-4';
    const headerTextClass = isDenseModern ? 'text-[10px]' : 'text-xs';
    const compactHeaderClass = compact ? 'whitespace-nowrap' : '';
    const compactCellClass = compact
        ? compactCellWrap
            ? isDenseModern
                ? 'align-top max-w-[8rem] sm:max-w-[10rem] md:max-w-[12rem] break-words whitespace-normal text-left leading-tight'
                : 'align-top max-w-[11rem] sm:max-w-[14rem] md:max-w-[18rem] break-words whitespace-normal text-left leading-snug'
            : 'whitespace-nowrap max-w-[120px] truncate'
        : '';

    const tableContainerClass = modern
        ? 'overflow-x-auto overflow-hidden rounded-xl border border-teal-100/80 bg-white shadow-sm dark:border-teal-900/40 dark:bg-gray-800'
        : 'overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800';

    const tableContent = (
        <table className={`w-full ${tableTextClass} text-left border-collapse ${fixedTableLayout ? 'table-fixed' : ''}`}>
                    <thead className={modern
                        ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white border-b border-teal-700/30'
                        : 'bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700'}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className={`${headerPaddingClass} ${compactHeaderClass} ${headerTextClass} font-bold uppercase tracking-wide border-r last:border-r-0 cursor-pointer transition-colors ${
                                          modern
                                            ? 'border-white/15 text-white hover:bg-white/10'
                                            : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/70'
                                        } ${header.column.columnDef.headerClassName || ''}`}
                                        style={header.getSize() !== 150 ? { width: header.getSize() } : undefined}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <div className="flex items-center gap-1 justify-center">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{
                                                asc: <ArrowUpIcon className={`w-2.5 h-2.5 ${modern ? 'text-white' : 'text-highlight-500'}`} />,
                                                desc: <ArrowDownIcon className={`w-2.5 h-2.5 ${modern ? 'text-white' : 'text-highlight-500'}`} />,
                                            }[header.column.getIsSorted()] ?? null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/80">
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length} className={`text-center text-gray-500 dark:text-gray-400 italic ${isDenseModern ? 'px-4 py-6' : 'px-6 py-12'}`}>
                                    {t('loading') || 'Veriler yükleniyor...'}
                                </td>
                            </tr>
                        ) : table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className={`text-center text-gray-500 dark:text-gray-400 italic ${isDenseModern ? 'px-4 py-6' : 'px-6 py-12'}`}>
                                    {t('noRecordsFound') || 'Kayıt bulunamadı.'}
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row, rowIndex) => {
                                const currentRowId = row?.original?.[rowIdAccessor];
                                const isSelected = selectedRowId !== null && selectedRowId !== undefined &&
                                    String(currentRowId) === String(selectedRowId);
                                const customRowClass = typeof getRowClassName === 'function'
                                    ? getRowClassName(row.original) || ''
                                    : '';

                                const getRowBackgroundClass = () => {
                                    if (isSelected) {
                                        return modern
                                            ? 'bg-teal-50 dark:bg-teal-900/20'
                                            : 'bg-blue-50 dark:bg-blue-900/20';
                                    }

                                    if (softStriped) {
                                        const isAltRow = rowIndex % 2 === 1;
                                        if (modern) {
                                            return isAltRow
                                                ? 'bg-teal-50/70 dark:bg-teal-950/25'
                                                : 'bg-white dark:bg-gray-800/35';
                                        }
                                        return isAltRow
                                            ? 'bg-slate-50 dark:bg-gray-700/30'
                                            : 'bg-white dark:bg-gray-800/25';
                                    }

                                    return 'bg-white dark:bg-gray-800/20';
                                };

                                const rowBackgroundClass = getRowBackgroundClass();
                                const hoverClass = modern
                                    ? 'hover:bg-teal-100/55 dark:hover:bg-teal-900/25'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/40';

                                return (
                                    <tr
                                        key={row.id}
                                        className={`${rowBackgroundClass} ${customRowClass} ${hoverClass} transition-colors cursor-pointer group`}
                                        onClick={() => onRowClick && onRowClick(row.original)}
                                        onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row.original)}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className={`${cellPaddingClass} ${compactCellClass} border-r border-gray-100 dark:border-gray-700/60 last:border-r-0 text-gray-900 dark:text-gray-100 ${cell.column.columnDef.cellClassName || ''}`}
                                                style={cell.column.getSize() !== 150 ? { width: cell.column.getSize() } : undefined}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
        </table>
    );

    const tableContainer = fixedHorizontalScroll ? (
        <FixedHorizontalScroll className={tableContainerClass}>{tableContent}</FixedHorizontalScroll>
    ) : (
        <div className={tableContainerClass}>{tableContent}</div>
    );

    return (
        <div className={modern ? 'space-y-2' : 'space-y-4'}>
            {/* Standard Search Bar */}
            {showSearch && (
                <div className="relative max-w-sm mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    </div>
                    <input
                        value={globalFilter ?? ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        className="block w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm transition-all focus:border-primary-400 dark:focus:border-primary-600 outline-none text-gray-900 dark:text-gray-100"
                        placeholder={t('searchPlaceholder') || 'Ara...'}
                    />
                </div>
            )}

            {/* Table Container - aligned with Quality Management / list screens */}
            {tableContainer}

            <div className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest text-right px-2">
                {table.getPrePaginationRowModel().rows.length} {t('recordsListed') || 'KAYIT LİSTELENDİ'}
            </div>

            {enablePagination && (
                <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-1">
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <span>Sayfa başına</span>
                        <select
                            value={table.getState().pagination.pageSize}
                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                            className="border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span>
                            Toplam {table.getPrePaginationRowModel().rows.length} kayıt
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className="px-2.5 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Önceki
                        </button>
                        <span className="text-xs text-gray-700 dark:text-gray-200">
                            {table.getState().pagination.pageIndex + 1} / {table.getPageCount() || 1}
                        </span>
                        <button
                            type="button"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className="px-2.5 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Sonraki
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
